import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Languages, Accessibility, Info, Globe, BookOpenCheck } from "lucide-react";

// Editing these lists? They live intentionally in the frontend so the page
// loads instantly (no backend fetch). If you find yourself updating this
// often, consider moving it behind an /api/translated-accessible endpoint.
const KEYS_PAS_LANGUAGES = [
  "English", "Arabic", "Canadian", "Dutch", "French", "French Canadian",
  "Hebrew", "Polish", "Portuguese", "Spanish", "Turkish",
];

const ADULT_OLA_LANGUAGES = [
  "Arabic", "Dutch", "Catalan (Spain)", "French", "German", "Italian",
  "Japanese", "Korean", "Polish", "Portuguese", "Russian",
  "Spanish (Lat Am)", "Spanish (Mexico)", "Turkish",
];

const ACCESSIBILITY_ITEMS = [
  {
    name: "Adult OLA",
    note: "Available with a screen reader for visually-impaired users.",
  },
  {
    name: "Career Compass",
    note: "Designed with accessibility in mind — screen-reader ready out of the box.",
  },
];


function LanguageChips({ items, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {items.map((lang) => (
        <span
          key={lang}
          className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-bold text-tc-ink shadow-sm"
        >
          {lang}
        </span>
      ))}
    </div>
  );
}


function ProductBlock({ icon: Icon, iconColor, title, languageCount, languages, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="tc-card p-6 sm:p-8"
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: iconColor + "20" }}
        >
          <Icon size={22} style={{ color: iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="tc-eyebrow text-slate-500">Translated in {languageCount} languages</div>
          <h2 className="font-display text-2xl sm:text-3xl text-tc-ink leading-tight mt-1">{title}</h2>
        </div>
      </div>
      <div className="mt-5">
        <LanguageChips items={languages} testid={`${testid}-chips`} />
      </div>
    </motion.section>
  );
}


export default function TranslatedAccessible() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen bg-tc-canvas"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink transition-colors"
          data-testid="back-to-hub"
        >
          <ArrowLeft size={14} /> Back to Hub
        </Link>

        <header className="mt-4">
          <div className="tc-eyebrow text-[#7ac143]">Translated &amp; Accessible Products</div>
          <h1 className="font-display text-4xl sm:text-5xl text-tc-ink mt-1 leading-tight" data-testid="ta-page-title">
            Products available in other languages and accessible formats.
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            True Colors materials are available in a growing set of languages and accessible formats.
            Reach out early so we can line up the right version for your client.
          </p>
        </header>

        {/* Advance-notice banner */}
        <div
          className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-4 sm:p-5 flex items-start gap-3"
          data-testid="ta-advance-notice"
        >
          <Info size={20} className="text-orange-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-lg text-tc-ink leading-tight">Advance notice may be required.</div>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">
              Translated and accessible versions aren&apos;t always in immediate stock.
              Please give us a heads-up so we can source the right materials in time for your program.
            </p>
          </div>
        </div>

        {/* Products */}
        <div className="mt-8 space-y-6">
          <ProductBlock
            icon={BookOpenCheck}
            iconColor="#f68b1f"
            title="Key to Personal Awareness and Success"
            languageCount={KEYS_PAS_LANGUAGES.length}
            languages={KEYS_PAS_LANGUAGES}
            testid="ta-keys-pas"
          />
          <ProductBlock
            icon={Globe}
            iconColor="#0091be"
            title="Adult OLA"
            languageCount={ADULT_OLA_LANGUAGES.length}
            languages={ADULT_OLA_LANGUAGES}
            testid="ta-adult-ola"
          />

          {/* Accessibility */}
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
            className="tc-card p-6 sm:p-8"
            data-testid="ta-accessibility"
          >
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: "#7ac14320" }}
              >
                <Accessibility size={22} style={{ color: "#7ac143" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="tc-eyebrow text-slate-500">Accessibility</div>
                <h2 className="font-display text-2xl sm:text-3xl text-tc-ink leading-tight mt-1">Screen-reader ready.</h2>
              </div>
            </div>
            <ul className="mt-5 space-y-3">
              {ACCESSIBILITY_ITEMS.map((item) => (
                <li
                  key={item.name}
                  className="rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3"
                  data-testid={`ta-a11y-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <Languages size={18} className="text-[#7ac143] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-tc-ink">{item.name}</div>
                    <div className="text-sm text-slate-600 leading-relaxed mt-0.5">{item.note}</div>
                  </div>
                </li>
              ))}
            </ul>
          </motion.section>
        </div>

        <div className="mt-10 text-center text-xs text-slate-400">
          Missing a language or format your client needs? Ask — we&apos;re always adding.
        </div>
      </div>
    </motion.div>
  );
}
